/**
 * Promotes the demo's Cognito identity from the *unauthenticated* (guest) role to the
 * *authenticated* role, which is what moves `execute-api:Invoke` off the role Shepherd
 * `CloudSecDetections:CognitoUnauthRoleOverlyPrivileged` inspects (issues `0eed7582`
 * devo / `7e96d579` prod).
 *
 * Each browser signs itself up as its own throwaway user and authenticates with
 * `CUSTOM_AUTH`, so no credential exists in source or in the bundle, and every browser
 * gets its own `IdentityId` — the per-caller attribution the CoE-381230 IDOR ownership
 * binding needs.
 *
 * Stage-generic: a stage with no user pool configured (currently prod) returns undefined
 * and the caller keeps guest behaviour, so this is safe to ship before every stage has a
 * pool.
 */

import AWS from 'aws-sdk';
import Environment from 'src/types/Environment';

const REGION = 'us-east-1';
const STORAGE_KEY = 'demoUserId';
const CHALLENGE_ANSWER = 'DEMO_NOOP';

// ID tokens last 60 minutes; refresh a little early.
const TOKEN_REFRESH_MARGIN_MS = 50 * 60 * 1000;
let cachedToken: { token: string; expiresAt: number } | undefined;

function isEnabled(): boolean {
    return Boolean(Environment.userPoolId) && Boolean(Environment.userPoolClientId);
}

function randomHex(byteCount: number): string {
    const bytes = new Uint8Array(byteCount);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** `demo-YYYYMMDD-<32 hex>`. The date prefix is what the cleanup job filters on. */
function newUsername(): string {
    const day = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `demo-${day}-${randomHex(16)}`;
}

/**
 * `SignUp` requires a password even though `CUSTOM_AUTH` never verifies one, so this is
 * generated, used once and never retained.
 */
function throwawayPassword(): string {
    return `Aa1${randomHex(16)}`;
}

async function signUpNewUser(idp: AWS.CognitoIdentityServiceProvider): Promise<string> {
    const username = newUsername();
    await idp.signUp({
        ClientId: Environment.userPoolClientId,
        Username: username,
        Password: throwawayPassword(),
    }).promise();
    window.sessionStorage.setItem(STORAGE_KEY, username);
    return username;
}

/** Runs the one-round custom challenge and returns the ID token. */
async function authenticate(idp: AWS.CognitoIdentityServiceProvider, username: string): Promise<string> {
    const started = await idp.initiateAuth({
        AuthFlow: 'CUSTOM_AUTH',
        ClientId: Environment.userPoolClientId,
        AuthParameters: { USERNAME: username },
    }).promise();

    const completed = await idp.respondToAuthChallenge({
        ClientId: Environment.userPoolClientId,
        ChallengeName: 'CUSTOM_CHALLENGE',
        Session: started.Session,
        ChallengeResponses: { USERNAME: username, ANSWER: CHALLENGE_ANSWER },
    }).promise();

    const token = completed.AuthenticationResult && completed.AuthenticationResult.IdToken;
    if (!token) {
        throw new Error('CUSTOM_AUTH returned no IdToken');
    }
    return token;
}

/**
 * Returns the Cognito Identity `Logins` map, or undefined on a stage with no user pool.
 *
 * These are unsigned Cognito operations on a secret-less app client, so no AWS
 * credentials are needed — the same reason the adapter's existing `getId` call works
 * with none.
 */
export async function getLogins(): Promise<{ [provider: string]: string } | undefined> {
    if (!isEnabled()) {
        return undefined;
    }

    const provider = `cognito-idp.${REGION}.amazonaws.com/${Environment.userPoolId}`;

    if (cachedToken && cachedToken.expiresAt > Date.now()) {
        return { [provider]: cachedToken.token };
    }

    const idp = new AWS.CognitoIdentityServiceProvider({ region: REGION });
    const stored = window.sessionStorage.getItem(STORAGE_KEY);

    let token: string;
    if (stored) {
        try {
            token = await authenticate(idp, stored);
        } catch (err) {
            // The stored user is gone — most likely pruned by the cleanup job while this
            // tab stayed open. Sign up again rather than failing permanently.
            window.sessionStorage.removeItem(STORAGE_KEY);
            token = await authenticate(idp, await signUpNewUser(idp));
        }
    } else {
        token = await authenticate(idp, await signUpNewUser(idp));
    }

    cachedToken = { token, expiresAt: Date.now() + TOKEN_REFRESH_MARGIN_MS };
    return { [provider]: token };
}
